import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/utils.js';

export const Page = forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <main
      ref={ref}
      data-slot="page"
      className={cn('flex h-full min-h-0 flex-col bg-background text-foreground', className)}
      {...props}
    />
  ),
);
Page.displayName = 'Page';

export const PageHeader = forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <header
      ref={ref}
      data-slot="page-header"
      className={cn(
        'flex shrink-0 flex-wrap items-start justify-between gap-4 border-b border-border px-6 py-5',
        className,
      )}
      {...props}
    />
  ),
);
PageHeader.displayName = 'PageHeader';

export const PageTitleBlock = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="page-title-block"
      className={cn('min-w-0 space-y-1', className)}
      {...props}
    />
  ),
);
PageTitleBlock.displayName = 'PageTitleBlock';

export const PageTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h1
      ref={ref}
      data-slot="page-title"
      className={cn('truncate text-xl font-semibold leading-tight text-foreground', className)}
      {...props}
    />
  ),
);
PageTitle.displayName = 'PageTitle';

export const PageDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    data-slot="page-description"
    className={cn('max-w-3xl text-sm leading-relaxed text-muted-foreground', className)}
    {...props}
  />
));
PageDescription.displayName = 'PageDescription';

export const PageActions = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="page-actions"
      className={cn('flex shrink-0 flex-wrap items-center justify-end gap-2', className)}
      {...props}
    />
  ),
);
PageActions.displayName = 'PageActions';

export const PageContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="page-content"
      className={cn('min-h-0 flex-1 overflow-auto p-6', className)}
      {...props}
    />
  ),
);
PageContent.displayName = 'PageContent';

export const PageFooter = forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <footer
      ref={ref}
      data-slot="page-footer"
      className={cn(
        'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-4',
        className,
      )}
      {...props}
    />
  ),
);
PageFooter.displayName = 'PageFooter';
